/**
 * CR107: Layout Studio Page
 * CR116: Added real-time preview pane with 2-column layout
 * CR120: Redesigned with Page Styling tab, dock modes, and seasonal preview
 * CR150: Added post-action toasts with auto-switch for publish/activate
 * CR202: Added header validation and publish confirmation with warnings
 * CR221: Replaced left sidebar with compact preset picker bar for more space
 * CR220: Fixed Publish gating - allow republishing live preset after save
 * 
 * Admin page for configuring Deal Finder page layout.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { previewConfigStore } from "@/stores/previewConfigStore";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  LayoutGrid,
  Plus,
  Eye,
  Layers,
  Paintbrush,
  Settings2,
  Play,
  RotateCcw,
  WandSparkles,
  CalendarClock,
  Sparkles,
} from "lucide-react";
import { useLayoutStudio } from "@/hooks/useLayoutStudio";
import { useLayoutPreviewSource } from "@/hooks/useLayoutPreviewSource";
import { useAppAssets } from "@/hooks/useAppAssets";
import { 
  LayoutEditor, 
  PageStylingTab, 
  PreviewPanel,
  DealCardsTab,
  PresetPickerBar,
  ManagePresetsDrawer,
  PublishConfirmDialog,
  CanvasEditorDialog,
  LayoutTakeoverScheduleDialog,
} from "@/components/admin/layout-studio";
import type { LayoutPreset, LayoutJson, ActivationEnvironment, HeaderBlockConfig } from "@/lib/layout-studio/types";
import { DEFAULT_LAYOUT_JSON, DEFAULT_HEADER_CONFIG } from "@/lib/layout-studio/types";
import type { DealCardUXConfig } from "@/lib/layout-studio/dealCardUXTypes";
import { DEFAULT_DEAL_CARD_UX_CONFIG } from "@/lib/layout-studio/dealCardUXTypes";
import type { 
  BackgroundConfig, 
  TakeoverConfig, 
  PreviewDockMode,
} from "@/lib/layout-studio/pageStylingTypes";
import { 
  DEFAULT_BACKGROUND_CONFIG, 
  DEFAULT_TAKEOVER_CONFIG,
} from "@/lib/layout-studio/pageStylingTypes";
import { validateHeaderConfig } from "@/lib/layout-studio/headerValidation";

export default function LayoutStudio() {
  const {
    presets,
    activations,
    auditLog,
    loading,
    saving,
    createPreset,
    updatePreset,
    archivePreset,
    clonePreset,
    deletePreset,
    activatePreset,
    rollback,
    getActivePreset,
    refetch,
  } = useLayoutStudio();

  // CR150: Preview source control for auto-switch
  const { setSource } = useLayoutPreviewSource();

  // CR202: Fetch assets for header validation
  const { assets: allAssets, loading: assetsLoading } = useAppAssets({ status: "active" });

  // Selected preset for editing
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [draftLayout, setDraftLayout] = useState<LayoutJson>(DEFAULT_LAYOUT_JSON);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // CR120: Page styling state
  const [draftBackground, setDraftBackground] = useState<BackgroundConfig>(DEFAULT_BACKGROUND_CONFIG);
  const [draftTakeover, setDraftTakeover] = useState<TakeoverConfig>(DEFAULT_TAKEOVER_CONFIG);

  // CR221: UI state - removed sidebar, using dock mode and preset drawer
  const [dockMode, setDockMode] = useState<PreviewDockMode>("right");
  const [activeConfigTab, setActiveConfigTab] = useState<"layout" | "styling" | "deal-cards">("layout");
  const [showManageDrawer, setShowManageDrawer] = useState(false);
  const previewVersionRef = useRef(0);

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState<ActivationEnvironment | null>(null);
  const [showCanvasEditor, setShowCanvasEditor] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [isBeginnerMode, setIsBeginnerMode] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  const [newPresetDesc, setNewPresetDesc] = useState("");

  // CR310: Read prefillHeight from URL params
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefillHeight, setPrefillHeight] = useState<number | null>(() => {
    const h = searchParams.get("prefillHeight");
    return h ? parseInt(h, 10) : null;
  });

  const handleApplyPrefillHeight = useCallback(() => {
    if (!prefillHeight) return;
    const currentUX = draftLayout.dealCardUX || DEFAULT_DEAL_CARD_UX_CONFIG;
    const newLayout: LayoutJson = {
      ...draftLayout,
      dealCardUX: {
        ...currentUX,
        customHeightsEnabled: true,
        customHeightsPx: {
          ...(currentUX.customHeightsPx || {}),
          compact: prefillHeight,
          regular: prefillHeight,
          large: prefillHeight,
        },
      },
    };
    setDraftLayout(newLayout);
    setHasUnsavedChanges(true);
    previewVersionRef.current += 1;
    previewConfigStore.setDealCardUX(newLayout.dealCardUX!);
    toast.success(`Applied ${prefillHeight}px card height`);
    setPrefillHeight(null);
    // Remove param from URL
    searchParams.delete("prefillHeight");
    setSearchParams(searchParams, { replace: true });
  }, [prefillHeight, draftLayout, searchParams, setSearchParams]);

  const handleDismissPrefillHeight = useCallback(() => {
    setPrefillHeight(null);
    searchParams.delete("prefillHeight");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Get current selections
  const selectedPreset = presets.find((p) => p.id === selectedPresetId) || null;
  const stagingPreset = getActivePreset("staging");
  const publishPreset = getActivePreset("publish");
  
  // CR202: Header validation for current draft layout
  const headerValidation = useMemo(() => {
    const headerBlock = draftLayout.blocks.find((b) => b.id === "header");
    const headerConfig = (headerBlock?.config as HeaderBlockConfig) || DEFAULT_HEADER_CONFIG;
    return validateHeaderConfig(headerBlock, headerConfig, allAssets);
  }, [draftLayout.blocks, allAssets]);

  // Auto-select first preset on load
  useEffect(() => {
    if (!loading && presets.length > 0 && !selectedPresetId) {
      const livePreset = publishPreset || stagingPreset || presets[0];
      if (livePreset) {
        setSelectedPresetId(livePreset.id);
        setDraftLayout(livePreset.layout_json || DEFAULT_LAYOUT_JSON);
      }
    }
  }, [loading, presets, selectedPresetId, publishPreset, stagingPreset]);

  // Handle preset selection
  const handleSelectPreset = useCallback((preset: LayoutPreset) => {
    if (hasUnsavedChanges) {
      if (!confirm("You have unsaved changes. Discard them?")) return;
    }
    setSelectedPresetId(preset.id);
    setDraftLayout(preset.layout_json || DEFAULT_LAYOUT_JSON);
    setDraftBackground(DEFAULT_BACKGROUND_CONFIG);
    setDraftTakeover(DEFAULT_TAKEOVER_CONFIG);
    setHasUnsavedChanges(false);
    setLastSavedAt(null);
  }, [hasUnsavedChanges]);

  // Handle preset selection by ID
  const handleSelectPresetById = useCallback((id: string) => {
    const preset = presets.find(p => p.id === id);
    if (preset) handleSelectPreset(preset);
  }, [presets, handleSelectPreset]);

  // Handle layout change
  const handleLayoutChange = useCallback((newLayout: LayoutJson) => {
    setDraftLayout(newLayout);
    setHasUnsavedChanges(true);
    previewVersionRef.current += 1;
    previewConfigStore.setLayout(newLayout);
  }, []);

  // Handle background change
  const handleBackgroundChange = useCallback((config: BackgroundConfig) => {
    setDraftBackground(config);
    setHasUnsavedChanges(true);
    previewVersionRef.current += 1;
  }, []);

  // Handle takeover change
  const handleTakeoverChange = useCallback((config: TakeoverConfig) => {
    setDraftTakeover(config);
    setHasUnsavedChanges(true);
    previewVersionRef.current += 1;
  }, []);

  // CR130: Handle deal card UX config change
  const handleDealCardUXChange = useCallback((config: DealCardUXConfig) => {
    const newLayout = {
      ...draftLayout,
      dealCardUX: config,
    };
    setDraftLayout(newLayout);
    setHasUnsavedChanges(true);
    previewVersionRef.current += 1;
    previewConfigStore.setDealCardUX(config);
  }, [draftLayout]);

  // Sync initial layout to preview store
  useEffect(() => {
    if (draftLayout) {
      previewConfigStore.setLayout(draftLayout);
    }
  }, []);

  // CR220: Compute publish gate state
  const publishGateState = useMemo(() => {
    const isStagingActive = stagingPreset?.id === selectedPresetId;
    const isPublishActive = publishPreset?.id === selectedPresetId;
    
    // Determine if Publish should be disabled and why
    let stagingDisabled = false;
    let stagingReason = "";
    let publishDisabled = false;
    let publishReason = "";
    
    if (saving) {
      stagingDisabled = true;
      stagingReason = "Saving in progress...";
      publishDisabled = true;
      publishReason = "Saving in progress...";
    } else if (hasUnsavedChanges) {
      stagingDisabled = true;
      stagingReason = "Save changes first";
      publishDisabled = true;
      publishReason = "Save changes first";
    }
    
    return {
      stagingDisabled,
      stagingReason,
      publishDisabled,
      publishReason,
      isStagingActive,
      isPublishActive,
    };
  }, [saving, hasUnsavedChanges, stagingPreset?.id, publishPreset?.id, selectedPresetId]);

  // CR220 Debug: Log publish gate state
  useEffect(() => {
    console.log("[PublishGate]", { 
      selectedPresetId,
      stagingPresetId: stagingPreset?.id,
      publishPresetId: publishPreset?.id,
      saving,
      hasUnsavedChanges,
      lastSavedAt: lastSavedAt?.toISOString(),
      ...publishGateState,
    });
  }, [selectedPresetId, stagingPreset?.id, publishPreset?.id, saving, hasUnsavedChanges, lastSavedAt, publishGateState]);

  // Compute preview key
  const previewKey = useMemo(() => {
    const stylingHash = `${draftBackground.type}-${draftTakeover.themeId || 'none'}`;
    const detailsSectionsHash = draftLayout.dealCardUX?.detailsSections
      ?.map(s => `${s.key}:${s.enabled}:${s.order}:${s.flipMaxLines ?? 0}`)
      .join(',') || '';
    const customHeightsHash = draftLayout.dealCardUX?.customHeightsEnabled 
      ? `${draftLayout.dealCardUX.customHeightsPx?.compact}-${draftLayout.dealCardUX.customHeightsPx?.regular}-${draftLayout.dealCardUX.customHeightsPx?.large}`
      : 'default';
    const feedbackWidgetHash = `${draftLayout.dealCardUX?.feedbackWidget?.enabled ?? false}-${draftLayout.dealCardUX?.feedbackWidget?.placement ?? "above_card"}`;
    const cardUXHash = `${draftLayout.dealCardUX?.cardSize}-${draftLayout.dealCardUX?.density}-${draftLayout.dealCardUX?.detailsMode}-${draftLayout.dealCardUX?.expandSingleOpen}-${detailsSectionsHash}-${customHeightsHash}-${feedbackWidgetHash}`;
    return `${selectedPresetId}-${previewVersionRef.current}-${JSON.stringify(draftLayout.typography)}-${draftLayout.blocks.filter(b => b.enabled).length}-${stylingHash}-${cardUXHash}`;
  }, [selectedPresetId, draftLayout.typography, draftLayout.blocks, draftLayout.dealCardUX, draftBackground, draftTakeover]);

  // Save draft
  const handleSave = useCallback(async () => {
    if (!selectedPresetId) return false;
    const success = await updatePreset(selectedPresetId, { layout_json: draftLayout });
    if (success) {
      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());
    }
    return success;
  }, [selectedPresetId, draftLayout, updatePreset]);

  // Create new preset
  const handleCreate = useCallback(async () => {
    if (!newPresetName.trim()) return;
    const preset = await createPreset(newPresetName.trim(), newPresetDesc.trim() || undefined);
    if (preset) {
      setShowCreateDialog(false);
      setNewPresetName("");
      setNewPresetDesc("");
      handleSelectPreset(preset);
    }
  }, [newPresetName, newPresetDesc, createPreset, handleSelectPreset]);

  // Clone current preset
  const handleClone = useCallback(async () => {
    if (!selectedPresetId) return;
    await clonePreset(selectedPresetId);
  }, [selectedPresetId, clonePreset]);

  // Archive current preset
  const handleArchive = useCallback(async () => {
    if (!selectedPresetId) return;
    if (!confirm("Archive this preset? It will be removed from the list.")) return;
    await archivePreset(selectedPresetId);
    setSelectedPresetId(null);
  }, [selectedPresetId, archivePreset]);

  // CR290D: Discard changes handler
  const handleDiscard = useCallback(() => {
    if (!selectedPreset) return;
    if (!confirm("Discard all unsaved changes?")) return;
    setDraftLayout(selectedPreset.layout_json || DEFAULT_LAYOUT_JSON);
    setHasUnsavedChanges(false);
    toast.info("Changes discarded", { description: "Reverted to last saved state." });
  }, [selectedPreset]);

  const handleSaveAndPublish = useCallback(async () => {
    if (!selectedPresetId || !showActivateDialog) return;
    
    // Save first
    const saveSuccess = await handleSave();
    if (!saveSuccess) {
      toast.error("Save failed", { description: "Could not save preset before publishing." });
      return;
    }
    
    // Then activate
    const success = await activatePreset(selectedPresetId, showActivateDialog);
    setShowActivateDialog(null);
    
    if (success) {
      const envLabel = showActivateDialog === "publish" ? "Live" : "Staging";
      toast.success(showActivateDialog === "publish" ? "Published" : "Activated", {
        description: `${selectedPreset?.name} is now active for ${envLabel}.`,
      });
      
      if (showActivateDialog === "publish") {
        setSource("published_live");
      } else {
        setSource("published_staging");
      }
    }
  }, [showActivateDialog, selectedPresetId, handleSave, activatePreset, selectedPreset?.name, setSource]);

  // CR221: Publish last saved handler
  const handlePublishLastSaved = useCallback(async () => {
    if (!selectedPresetId || !showActivateDialog) return;
    
    const success = await activatePreset(selectedPresetId, showActivateDialog);
    setShowActivateDialog(null);
    
    if (success) {
      const envLabel = showActivateDialog === "publish" ? "Live" : "Staging";
      toast.success(showActivateDialog === "publish" ? "Published" : "Activated", {
        description: `${selectedPreset?.name} is now active for ${envLabel}.`,
      });
      
      if (showActivateDialog === "publish") {
        setSource("published_live");
      } else {
        setSource("published_staging");
      }
    }
  }, [showActivateDialog, selectedPresetId, activatePreset, selectedPreset?.name, setSource]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 gap-6">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-full flex flex-col">
        {/* CR221: Compact header with preset picker bar */}
        <div className="border-b flex-shrink-0">
          <div className="px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LayoutGrid className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">Layout Studio</h1>
            </div>
            
            {/* Activation buttons */}
            {selectedPreset && (
              <TooltipProvider delayDuration={300}>
                <div className="flex items-center gap-2">
                  {/* Beginner/Advanced Toggle */}
                  <div className="flex items-center rounded-md border bg-muted/40 p-0.5">
                    <button
                      className={`px-2.5 py-1 text-xs font-medium rounded-sm transition-all flex items-center gap-1 ${
                        isBeginnerMode
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={() => setIsBeginnerMode(true)}
                    >
                      <Sparkles className="h-3 w-3" />
                      Beginner
                    </button>
                    <button
                      className={`px-2.5 py-1 text-xs font-medium rounded-sm transition-all flex items-center gap-1 ${
                        !isBeginnerMode
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={() => setIsBeginnerMode(false)}
                    >
                      <Settings2 className="h-3 w-3" />
                      Advanced
                    </button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCanvasEditor(true)}
                    disabled={!selectedPreset}
                  >
                    <WandSparkles className="h-4 w-4 mr-1" />
                    Open Canvas Editor
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowScheduleDialog(true)}
                    disabled={!selectedPreset}
                  >
                    <CalendarClock className="h-4 w-4 mr-1" />
                    Schedule
                  </Button>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowActivateDialog("staging")}
                          disabled={publishGateState.stagingDisabled}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Staging
                          {publishGateState.isStagingActive && (
                            <Badge variant="secondary" className="ml-1 text-[10px] px-1">Active</Badge>
                          )}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {publishGateState.stagingReason && (
                      <TooltipContent side="bottom">
                        <p>{publishGateState.stagingReason}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          size="sm"
                          onClick={() => setShowActivateDialog("publish")}
                          disabled={publishGateState.publishDisabled}
                        >
                          <Play className="h-4 w-4 mr-1" />
                          Publish
                          {publishGateState.isPublishActive && (
                            <Badge variant="secondary" className="ml-1 text-[10px] px-1">Live</Badge>
                          )}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {publishGateState.publishReason && (
                      <TooltipContent side="bottom">
                        <p>{publishGateState.publishReason}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                  
                  {(stagingPreset || publishPreset) && (
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => rollback("staging")}
                        disabled={saving}
                        className="h-8 px-2"
                      >
                        <RotateCcw className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </TooltipProvider>
            )}
          </div>
          
          {/* Preset Picker Bar */}
          <PresetPickerBar
            presets={presets}
            selectedPresetId={selectedPresetId}
            stagingPresetId={stagingPreset?.id ?? null}
            publishPresetId={publishPreset?.id ?? null}
            hasUnsavedChanges={hasUnsavedChanges}
            saving={saving}
            lastSavedAt={lastSavedAt}
            onSelectPreset={handleSelectPresetById}
            onCreateNew={() => setShowCreateDialog(true)}
            onClone={handleClone}
            onArchive={handleArchive}
            onSave={handleSave}
            onDiscard={handleDiscard}
            onManagePresets={() => setShowManageDrawer(true)}
          />
        </div>

        {/* CR221: 2-Panel Layout (no sidebar) */}
        <div className={`flex-1 flex overflow-hidden ${dockMode === "bottom" ? "flex-col" : ""}`}>
          {/* Main Content: Config Editor */}
          <div className={`flex-1 overflow-auto ${dockMode === "right" ? "border-r" : "border-b"}`}>
            {selectedPreset ? (
              <div className="p-4 space-y-4">
                {/* CR310: Prefill height banner from Builder */}
                {prefillHeight && (
                  <Alert className="border-primary/50 bg-primary/5">
                    <AlertDescription className="flex items-center justify-between">
                      <span className="text-sm">
                        Use prefilled height <span className="font-mono font-bold">{prefillHeight}px</span> from Builder?
                      </span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleDismissPrefillHeight}>
                          Dismiss
                        </Button>
                        <Button size="sm" className="h-7 text-xs" onClick={handleApplyPrefillHeight}>
                          Apply
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                {/* Config Tabs */}
                <Tabs value={activeConfigTab} onValueChange={(v) => setActiveConfigTab(v as "layout" | "styling" | "deal-cards")}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="layout" className="gap-2">
                      <Settings2 className="h-4 w-4" />
                      Layout
                    </TabsTrigger>
                    <TabsTrigger value="styling" className="gap-2">
                      <Paintbrush className="h-4 w-4" />
                      Page Styling
                    </TabsTrigger>
                    <TabsTrigger value="deal-cards" className="gap-2">
                      <Layers className="h-4 w-4" />
                      Deal Cards
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="layout" className="mt-4">
                    <LayoutEditor
                      layout={draftLayout}
                      onChange={handleLayoutChange}
                    />
                  </TabsContent>
                  
                  <TabsContent value="styling" className="mt-4">
                    <PageStylingTab
                      background={draftBackground}
                      takeover={draftTakeover}
                      onBackgroundChange={handleBackgroundChange}
                      onTakeoverChange={handleTakeoverChange}
                    />
                  </TabsContent>
                  
                  <TabsContent value="deal-cards" className="mt-4">
                    <DealCardsTab
                      config={draftLayout.dealCardUX || DEFAULT_DEAL_CARD_UX_CONFIG}
                      onChange={handleDealCardUXChange}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <LayoutGrid className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p className="font-medium">Select a preset to edit</p>
                  <p className="text-sm">or create a new one</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Preset
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right/Bottom Panel: Preview - more space now */}
          <div className={`flex-shrink-0 ${dockMode === "right" ? "w-[560px]" : "h-[400px] w-full"}`}>
            {selectedPreset ? (
              <PreviewPanel
                layout={draftLayout}
                previewKey={previewKey}
                dockMode={dockMode}
                background={draftBackground}
                takeover={draftTakeover}
                onDockModeChange={setDockMode}
                selectedPresetName={selectedPreset.name}
              />
            ) : (
              <Card className="h-full flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <Eye className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Select a preset to see preview</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Layout Preset</DialogTitle>
            <DialogDescription>
              Start with default layout settings. You can customize after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                placeholder="e.g., Holiday Layout"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={newPresetDesc}
                onChange={(e) => setNewPresetDesc(e.target.value)}
                placeholder="Describe this layout variant..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!newPresetName.trim() || saving}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CR221: Manage Presets Drawer */}
      <ManagePresetsDrawer
        open={showManageDrawer}
        onOpenChange={setShowManageDrawer}
        presets={presets}
        stagingPresetId={stagingPreset?.id ?? null}
        publishPresetId={publishPreset?.id ?? null}
        onSelectPreset={handleSelectPreset}
        onClonePreset={clonePreset}
        onArchivePreset={archivePreset}
        onDeletePreset={deletePreset}
        onRenamePreset={async (id, name) => updatePreset(id, { name })}
      />

      {/* CR221: Publish Confirm Dialog with unsaved changes guardrail */}
      <PublishConfirmDialog
        open={!!showActivateDialog}
        onOpenChange={(open) => !open && setShowActivateDialog(null)}
        environment={showActivateDialog || "staging"}
        presetName={selectedPreset?.name || ""}
        hasUnsavedChanges={hasUnsavedChanges}
        headerWarnings={headerValidation.warnings}
        lastSavedAt={lastSavedAt || selectedPreset?.updated_at}
        saving={saving}
        onSaveAndPublish={handleSaveAndPublish}
        onPublishLastSaved={handlePublishLastSaved}
        onCancel={() => setShowActivateDialog(null)}
      />

      <CanvasEditorDialog
        open={showCanvasEditor}
        onOpenChange={setShowCanvasEditor}
        layout={draftLayout}
        presetName={selectedPreset?.name || ""}
        onChange={handleLayoutChange}
        onSave={handleSave}
        onOpenSchedule={() => setShowScheduleDialog(true)}
        isBeginnerMode={isBeginnerMode}
      />

      <LayoutTakeoverScheduleDialog
        open={showScheduleDialog}
        onOpenChange={setShowScheduleDialog}
        presets={presets}
        selectedPresetId={selectedPresetId}
      />
    </AdminLayout>
  );
}
